import {
  Card,
  Table,
  Typography,
  Tag,
  Skeleton,
  Descriptions,
  Divider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useWorkflowHistory } from "../../hooks/useWorkflowHistory";
import type {
  WorkflowHistoryActivity,
  WorkflowHistoryAssignment,
} from "../../types";

const { Title, Text, Link } = Typography;

/** Data passed when clicking vote */
export interface VoteClickData {
  activityId: string;
  assignmentId: string;
  activityName: string;
  assignedTo: string;
  instructions?: string;
}

interface WorkflowHistoryReportProps {
  itemType: string;
  itemId: string;
  /** Enable fetching data */
  enabled?: boolean;
  /** Callback when "VOTE NOW" is clicked */
  onVoteClick?: (data: VoteClickData) => void;
  /**
   * Whether to show the Card wrapper with title.
   * Set to false when used inside another Card (e.g., RelationshipTabs).
   * Default: true
   */
  showTitle?: boolean;
}

interface FlattenedRow {
  key: string;
  activityId: string;
  activity: string;
  state: string;
  activeDate?: string;
  assignmentId: string;
  assignedTo: string;
  completedBy?: string;
  howVoted?: string;
  when?: string;
  comments?: string;
  instructions?: string;
  canVote: boolean;
  isDisabled: boolean;
  /** For row span calculation */
  isFirstInActivity: boolean;
  activityRowSpan: number;
}

/**
 * Workflow History Report component that displays the history of workflow activities
 * and their assignments, similar to Aras Innovator's "Workflow History Report".
 */
export const WorkflowHistoryReport: React.FC<WorkflowHistoryReportProps> = ({
  itemType,
  itemId,
  enabled = true,
  onVoteClick,
  showTitle = true,
}) => {
  const { t } = useTranslation();
  const { workflowHistory, isLoading, isError } = useWorkflowHistory({
    itemType,
    itemId,
    enabled,
  });

  // Flatten activities and assignments for table display
  const flattenedData: FlattenedRow[] = [];
  if (workflowHistory) {
    workflowHistory.activities.forEach((activity: WorkflowHistoryActivity) => {
      if (activity.assignments.length === 0) {
        // Activity with no assignments
        flattenedData.push({
          key: activity.id,
          activityId: activity.id,
          activity: activity.activity,
          state: activity.state,
          activeDate: activity.activeDate,
          assignmentId: "",
          assignedTo: "-",
          completedBy: undefined,
          howVoted: undefined,
          when: undefined,
          comments: undefined,
          instructions: undefined,
          canVote: false,
          isDisabled: false,
          isFirstInActivity: true,
          activityRowSpan: 1,
        });
      } else {
        activity.assignments.forEach(
          (assignment: WorkflowHistoryAssignment, index: number) => {
            flattenedData.push({
              key: `${activity.id}-${assignment.id}`,
              activityId: activity.id,
              activity: activity.activity,
              state: activity.state,
              activeDate: activity.activeDate,
              assignmentId: assignment.id,
              assignedTo: assignment.assignedTo,
              completedBy: assignment.completedBy,
              howVoted: assignment.howVoted,
              when: assignment.when,
              comments: assignment.comments,
              instructions: assignment.instructions,
              canVote: assignment.canVote,
              isDisabled: assignment.isDisabled,
              isFirstInActivity: index === 0,
              activityRowSpan: index === 0 ? activity.assignments.length : 0,
            });
          },
        );
      }
    });
  }

  const getStateColor = (state: string) => {
    switch (state.toLowerCase()) {
      case "active":
        return "processing";
      case "closed":
        return "success";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<FlattenedRow> = [
    {
      title: t("workflow.history.activity"),
      dataIndex: "activity",
      key: "activity",
      width: 150,
      onCell: (record) => ({
        rowSpan: record.activityRowSpan,
      }),
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: t("workflow.history.state"),
      dataIndex: "state",
      key: "state",
      width: 100,
      onCell: (record) => ({
        rowSpan: record.activityRowSpan,
      }),
      render: (state: string) => (
        <Tag color={getStateColor(state)}>{state}</Tag>
      ),
    },
    {
      title: t("workflow.history.assignedTo"),
      dataIndex: "assignedTo",
      key: "assignedTo",
      width: 150,
    },
    {
      title: t("workflow.history.completedBy"),
      dataIndex: "completedBy",
      key: "completedBy",
      width: 150,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: t("workflow.history.howVoted"),
      dataIndex: "howVoted",
      key: "howVoted",
      width: 120,
      render: (text: string | undefined, record: FlattenedRow) => {
        if (record.canVote && onVoteClick) {
          return (
            <Link
              onClick={() =>
                onVoteClick({
                  activityId: record.activityId,
                  assignmentId: record.assignmentId,
                  activityName: record.activity,
                  assignedTo: record.assignedTo,
                  instructions: record.instructions,
                })
              }
            >
              {t("workflow.history.voteNow")}
            </Link>
          );
        }
        return text || "-";
      },
    },
    {
      title: t("workflow.history.when"),
      dataIndex: "when",
      key: "when",
      width: 160,
      render: (date: string | undefined) =>
        date ? dayjs(date).format("M/D/YYYY h:mm:ss A") : "-",
    },
    {
      title: t("workflow.history.comments"),
      dataIndex: "comments",
      key: "comments",
      ellipsis: true,
      render: (text: string | undefined) => text || "-",
    },
  ];

  if (isLoading) {
    const loadingContent = <Skeleton active paragraph={{ rows: 4 }} />;
    if (!showTitle) {
      return loadingContent;
    }
    return <Card title={t("workflow.history.title")}>{loadingContent}</Card>;
  }

  // Don't render anything if there's no workflow data
  // Return empty fragment instead of null so parent can handle empty state
  if (isError || !workflowHistory) {
    return <></>;
  }

  // Report content (without Card wrapper)
  const reportContent = (
    <>
      {/* Report Header */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          {t("workflow.history.reportTitle")}
        </Title>

        <Descriptions
          column={{ xs: 1, sm: 2, md: 3 }}
          size="small"
          style={{ marginBottom: 8 }}
        >
          <Descriptions.Item label={t("workflow.history.item")} span={1}>
            <Text strong>{workflowHistory.itemName}</Text>
          </Descriptions.Item>
          <Descriptions.Item span={1}> </Descriptions.Item>
          <Descriptions.Item
            label={t("workflow.history.currentStatus")}
            span={1}
          >
            <Tag color={getStateColor(workflowHistory.currentStatus)}>
              {workflowHistory.currentStatus}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label={t("workflow.history.startedBy")} span={1}>
            {workflowHistory.startedBy || "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("workflow.history.startedOn")} span={1}>
            {workflowHistory.startedOn
              ? dayjs(workflowHistory.startedOn).format("M/D/YYYY h:mm:ss A")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("workflow.history.completedOn")} span={1}>
            {workflowHistory.completedOn
              ? dayjs(workflowHistory.completedOn).format("M/D/YYYY h:mm:ss A")
              : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <Divider style={{ margin: "16px 0" }} />

      {/* Activities Table */}
      <Table
        columns={columns}
        dataSource={flattenedData}
        pagination={false}
        size="small"
        bordered
        rowKey="key"
      />
    </>
  );

  // Conditionally wrap in Card
  if (!showTitle) {
    return reportContent;
  }

  return <Card title={t("workflow.history.title")}>{reportContent}</Card>;
};

export default WorkflowHistoryReport;
